import cn from "classnames";
import { memo, useCallback, useState } from "react";
import Highlighter from "react-highlight-words";
import InfiniteScroll from "react-infinite-scroll-component";
import { ArrowDownAZ, ArrowUpAZ, ChevronRight, Eye, Loader2, MoreVertical, Pencil, Plus, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useGetLexiconConceptBySubject } from "@noa/api-client";
import { useFilteredConcepts } from "@noa/api-client";
import type { LexiconConceptResponse } from "@noa/api-core";
import type { AppLanguageType } from "@noa/i18n";
import type { LexiconSubject } from "~/common/lexicon-subject";
import { Button, Dropdown, Input, Text, Tooltip } from "~/components/ui";

type LexiconSubjectProps = {
  language: AppLanguageType;
  subject: LexiconSubject;
  onAddConcept: (subjectId: string) => void;
  setConceptToView: React.Dispatch<React.SetStateAction<LexiconConceptResponse | null>>;
  setConceptToUpdate: React.Dispatch<React.SetStateAction<LexiconConceptResponse | null>>;
  setConceptToDelete: React.Dispatch<React.SetStateAction<LexiconConceptResponse | null>>;
};

export const LexiconSubjectConcepts = memo(function LexiconSubjectConcepts({
  language,
  subject,
  onAddConcept,
  setConceptToView,
  setConceptToUpdate,
  setConceptToDelete,
}: LexiconSubjectProps) {
  const { t } = useTranslation("lexicon");
  const [isOpen, setIsOpen] = useState(false);

  const isEnabled = isOpen;
  const { concepts, isLoadingConcepts, fetchNextPage, hasNextPage } = useGetLexiconConceptBySubject(
    language === "fr" ? subject.fr! : subject.en!,
    language,
    isEnabled
  );

  const { filteredConcepts, isSortAsc, setIsSortAsc, search, setSearch } = useFilteredConcepts(concepts, language);

  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const toggleExpanded = (id: string) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const subjectLabel = subject[language] ?? subject[language === "fr" ? "en" : "fr"] ?? "";

  // Mirrors the filter behind the create modal's options: only a subject holding
  // both translations can be preselected there.
  const canAddConcept = Boolean(subject.fr && subject.en);

  const handleAddConcept = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onAddConcept(subject.id);
    },
    [onAddConcept, subject.id]
  );

  return (
    <div className="border border-noa rounded-md">
      <div
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex cursor-pointer select-none items-center justify-between px-3 py-3 xs:px-6"
      >
        <div className="flex min-w-0 items-center gap-2">
          <ChevronRight
            className={cn(
              "transition-transform duration-200 text-base shrink-0 text-noa-muted",
              isOpen ? "rotate-90" : "rotate-0"
            )}
          />
          <Text strong className="truncate text-base xs:overflow-visible xs:text-clip xs:whitespace-normal">
            {subjectLabel}
          </Text>
        </div>
        {/* A concept carries both translations of its subject, so a half-translated
            one cannot receive a new concept — the create modal does not list it and
            submitting would fail on a subject the user never chose. Say that here
            rather than opening a modal that dead-ends.

            The span is load-bearing twice over, because a disabled <button>
            dispatches no pointer events at all: a Tooltip wrapped straight around
            it would never open, and the click that `handleAddConcept` would have
            stopped instead reaches the header and collapses the row. The wrapper
            takes both. */}
        <Tooltip title={canAddConcept ? undefined : t("errors.subjectNotTranslated")}>
          <span className={canAddConcept ? undefined : "cursor-not-allowed"} onClick={(e) => e.stopPropagation()}>
            <Button size="small" icon={<Plus size={14} />} onClick={handleAddConcept} disabled={!canAddConcept} />
          </span>
        </Tooltip>
      </div>
      {isOpen && (
        <div className="flex min-h-37.5 w-full flex-col gap-2 px-4 pt-2 pb-3">
          <div className="flex gap-2 pb-3">
            <Button
              size="small"
              icon={isSortAsc ? <ArrowUpAZ size={14} /> : <ArrowDownAZ size={14} />}
              onClick={() => setIsSortAsc((prev) => !prev)}
            />
            <Input
              size="small"
              allowClear
              placeholder={t("searchTermsPlaceholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="max-w-80"
            />
          </div>
          {isLoadingConcepts ? (
            <div className="flex justify-center p-4">
              <Loader2 size={48} className="animate-spin" />
            </div>
          ) : (
            <InfiniteScroll
              dataLength={concepts.length}
              next={fetchNextPage}
              hasMore={hasNextPage ?? false}
              loader={
                <div className="flex w-full justify-center p-4">
                  <Loader2 size={48} className="animate-spin" />
                </div>
              }
              scrollableTarget="lexicon-content"
              className="overflow-visible"
            >
              {filteredConcepts.map((concept) => {
                const synonyms = language === "fr" ? concept.synonyms_fr : concept.synonyms_en;
                const trimmedSearch = search.trim().toLowerCase();
                const hasSynonymMatch =
                  trimmedSearch !== "" && synonyms.some((s) => s.toLowerCase().includes(trimmedSearch));
                const isExpanded = expandedIds.has(concept.unique);
                return (
                  <div key={concept.unique} className="flex h-11 min-w-0 flex-col xs:pl-3">
                    <div className="flex min-h-6 min-w-0 items-center">
                      <Dropdown
                        trigger={["click"]}
                        menu={{
                          items: [
                            {
                              key: "view",
                              icon: <Eye size={14} />,
                              label: t("actions.view"),
                              onClick: () => setConceptToView(concept),
                            },
                            {
                              key: "edit",
                              icon: <Pencil size={14} />,
                              label: t("actions.update"),
                              onClick: () => setConceptToUpdate(concept),
                            },
                            {
                              key: "delete",
                              icon: <Trash2 size={14} />,
                              label: t("actions.delete"),
                              danger: true,
                              onClick: () => setConceptToDelete(concept),
                            },
                          ],
                        }}
                      >
                        <Button size="small" className="w-5 h-6 pt-px" icon={<MoreVertical size={14} />} />
                      </Dropdown>
                      <Text className="mx-2 shrink-0">
                        <Highlighter
                          autoEscape
                          searchWords={[search]}
                          textToHighlight={
                            language === "fr"
                              ? concept.term_fr.toLocaleUpperCase()
                              : concept.term_en.toLocaleUpperCase()
                          }
                        />
                      </Text>
                      {synonyms.length > 0 && (
                        <Button
                          onClick={() => toggleExpanded(concept.unique)}
                          className={cn(
                            "shrink-0 text-xs h-6 w-6 p-0 rounded-3xl",
                            hasSynonymMatch && "bg-noa-warning-subtle border-noa-warning-strong text-noa"
                          )}
                        >
                          {synonyms.length}
                        </Button>
                      )}
                      <Text ellipsis className="ml-2 min-w-0 flex-1 opacity-90">
                        <Highlighter
                          autoEscape
                          searchWords={[search]}
                          textToHighlight={language === "fr" ? concept.description_fr : concept.description_en}
                        />
                      </Text>
                    </div>
                    {synonyms.length > 0 && (
                      <Text
                        className={cn(
                          "pl-5 sm:pl-10 h-4 mt-px flex min-w-0 items-center gap-1 text-xs",
                          isExpanded ? "visible" : "invisible"
                        )}
                      >
                        <ChevronRight size={12} className="shrink-0" />
                        <span className="truncate">
                          <Highlighter autoEscape searchWords={[search]} textToHighlight={synonyms.join(", ")} />
                        </span>
                      </Text>
                    )}
                  </div>
                );
              })}
            </InfiniteScroll>
          )}
        </div>
      )}
    </div>
  );
});
